import { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import {
  BreadcrumbHeading,
  IOption,
  PrimaryButton,
  PrimaryButtonProps,
  Refresh,
  SecondaryButton,
} from '@weareopenr/component-library';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatDate } from '../../helpers/formatDate/formatDate';
import { formatTime } from '../../helpers/formatTime/formatTime';
import { IEndpoint } from '../../types/aggregator.types';
import { getSyncEndpoint } from 'src/helpers/getSyncEndpoint/getSyncEndpoint';
import useBreadcrumbs from 'src/hooks/useBreadcrumbs/useBreadcrumbs';
import { useExternalSystems } from 'src/hooks/useExternalSystems';
import { EProductLibraryStatus } from 'src/api/getProductLibrarySyncStatus';
import styles from './PageHeadingWithSync.module.scss';

export type IHeadingOptionMenuItem = IOption;

export interface IPageHeadingWithSyncProps {
  optionMenuItems: IHeadingOptionMenuItem[];
  entityLabel: string;
  syncEndpointName?: string;
  isSyncEnabled: boolean;
  isSyncInProgress: boolean;
  triggerSync: () => void;
  rejectSync: () => void;
  syncStatus?: EProductLibraryStatus;
  reviewRoutePath: string;
  renderReviewModal: (args: { isOpen: boolean; closeModal: () => void }) => ReactNode;
  primaryAction?: ReactElement<PrimaryButtonProps>;
  extraModals?: ReactNode;
}

const getLastSyncTime = (endpoint?: IEndpoint | null): Date | null => {
  if (endpoint?.lastUpdated) {
    return new Date(endpoint.lastUpdated);
  }
  return null;
};

export const PageHeadingWithSync = ({
  optionMenuItems,
  entityLabel,
  syncEndpointName,
  isSyncEnabled,
  isSyncInProgress,
  triggerSync,
  rejectSync,
  syncStatus,
  reviewRoutePath,
  renderReviewModal,
  primaryAction,
  extraModals,
}: IPageHeadingWithSyncProps): ReactElement => {
  const { data: externalSystems } = useExternalSystems();
  const navigate = useNavigate();
  const location = useLocation();
  const breadcrumbs = useBreadcrumbs();

  const searchParams = new URLSearchParams(location.search);
  const reviewModalRequested = searchParams.get('review') === 'true';

  const syncEndpoint = useMemo(
    () => getSyncEndpoint(externalSystems, syncEndpointName),
    [externalSystems, syncEndpointName],
  );
  const isSyncFeatureEnabled = useMemo(() => !!syncEndpoint && isSyncEnabled, [syncEndpoint, isSyncEnabled]);
  const lastSyncTime = getLastSyncTime(syncEndpoint);

  const [isReviewModalOpen, setIsReviewModalOpen] = useState<boolean>(false);

  const isPendingReview = syncStatus === EProductLibraryStatus.PENDING_REVIEW;

  useEffect(() => {
    if (reviewModalRequested && isPendingReview) {
      setIsReviewModalOpen(true);
    }
  }, [reviewModalRequested, isPendingReview]);

  const closeReviewModal = () => {
    setIsReviewModalOpen(false);
    if (reviewModalRequested) {
      navigate(reviewRoutePath);
    }
  };

  return (
    <>
      {renderReviewModal({ isOpen: isReviewModalOpen, closeModal: closeReviewModal })}
      {extraModals}
      <div className={styles['info-container']}>
        <BreadcrumbHeading
          breadcrumbs={breadcrumbs}
          optionMenuItems={optionMenuItems}
          primaryButton={
            isPendingReview ? (
              <PrimaryButton onClick={() => setIsReviewModalOpen(true)} icon={Refresh}>
                Review Sync
              </PrimaryButton>
            ) : (
              primaryAction
            )
          }
          secondaryButton={
            isSyncFeatureEnabled ? (
              isPendingReview ? (
                <SecondaryButton onClick={rejectSync} colour="tomato">
                  Reject Sync
                </SecondaryButton>
              ) : (
                <SecondaryButton onClick={triggerSync} icon={Refresh} disabled={isSyncInProgress}>
                  {isSyncInProgress ? 'Sync in Progress...' : `Sync ${entityLabel}`}
                </SecondaryButton>
              )
            ) : undefined
          }
          subtitle={
            lastSyncTime
              ? `Last synchronised at ${formatDate(lastSyncTime)}, ${formatTime(lastSyncTime)}`
              : 'Not yet synchronised'
          }
        />
      </div>
    </>
  );
};
